/**
 * DV-STAGE-003: Centralized Logging System with Ring Buffer
 * High-performance logging with memory management and export capabilities
 */

import { RingBuffer, createLogRingBuffer } from './ring-buffer';
import type { LogEntry } from '@/components/demo-viewer/LogsPanel';
import type { DemoLogLevel, DemoLogSource, DemoLogAction } from './demo-logger';

export interface EnhancedLogEntry extends LogEntry {
  id: string;
  source: DemoLogSource;
  action?: DemoLogAction;
  correlationId?: string;
  userId?: string;
  sessionId?: string;
  component?: string;
  performance?: {
    duration?: number;
    memoryUsage?: number;
    timestamp: number;
  };
}

export interface LoggerConfig {
  maxEntries: number;
  enablePerformanceTracking: boolean;
  enableAutoExport: boolean;
  autoExportThreshold: number;
  sources: DemoLogSource[];
  levels: DemoLogLevel[];
}

export interface LoggerStats {
  totalLogs: number;
  logsPerSource: Record<DemoLogSource, number>;
  logsPerLevel: Record<DemoLogLevel, number>;
  memoryUsageKB: number;
  bufferUtilization: number;
  sessionStartTime: string;
  lastLogTime?: string;
}

class CentralizedLogger {
  private ringBuffer: RingBuffer<EnhancedLogEntry>;
  private config: LoggerConfig;
  private sessionId: string;
  private sessionStartTime: string;
  private subscribers: Set<(entries: EnhancedLogEntry[]) => void> = new Set();
  private statsSubscribers: Set<(stats: LoggerStats) => void> = new Set();
  private logCounter: number = 0;
  private sourceCounters: Record<DemoLogSource, number> = {} as any;
  private levelCounters: Record<DemoLogLevel, number> = {} as any;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      maxEntries: 1000,
      enablePerformanceTracking: import.meta.env.DEV === true,
      enableAutoExport: false,
      autoExportThreshold: 0.9,
      sources: ['auth', 'websocket', 'temporal', 'heartbeats', 'feature-flags', 'stage-outputs', 'billing', 'api', 'ui', 'system', 'demo', 'user', 'stage_tester'],
      levels: ['debug', 'info', 'warn', 'error'],
      ...config
    };

    this.sessionId = this.generateSessionId();
    this.sessionStartTime = new Date().toISOString();

    // Initialize counters
    this.config.sources.forEach(source => {
      this.sourceCounters[source] = 0;
    });
    this.config.levels.forEach(level => {
      this.levelCounters[level] = 0;
    });

    this.ringBuffer = createLogRingBuffer(this.config.maxEntries);

    this.log('system', 'info', 'Centralized logger initialized', {
      config: this.config,
      sessionId: this.sessionId
    });
  }

  /**
   * Log an entry with enhanced metadata
   */
  log(
    source: DemoLogSource,
    level: DemoLogLevel,
    text: string,
    meta?: Record<string, any>,
    options?: {
      action?: DemoLogAction;
      correlationId?: string;
      component?: string;
      startTime?: number;
    }
  ): string {
    // Filter by configured sources and levels
    if (!this.config.sources.includes(source) || !this.config.levels.includes(level)) {
      return '';
    }

    const timestamp = new Date().toISOString();
    const id = this.generateLogId();
    const performanceData = this.config.enablePerformanceTracking ? {
      duration: options?.startTime ? Date.now() - options.startTime : undefined,
      memoryUsage: typeof window !== 'undefined' && (window as any).performance?.memory?.usedJSHeapSize || undefined,
      timestamp: Date.now()
    } : undefined;

    const entry: EnhancedLogEntry = {
      id,
      ts: timestamp,
      level,
      text,
      source,
      action: options?.action,
      correlationId: options?.correlationId,
      sessionId: this.sessionId,
      component: options?.component,
      meta: {
        ...meta,
        _internal: {
          entryNumber: this.logCounter++,
          sessionDuration: Date.now() - new Date(this.sessionStartTime).getTime()
        }
      },
      performance: performanceData
    };

    // Add to ring buffer
    this.ringBuffer.add(entry);

    // Update counters
    this.sourceCounters[source]++;
    this.levelCounters[level]++;

    // Check auto-export threshold
    if (this.config.enableAutoExport) {
      const bufferStats = this.ringBuffer.getStats();
      if (bufferStats.utilizationPercent >= this.config.autoExportThreshold * 100) {
        this.autoExport();
      }
    }

    // Notify subscribers
    this.notifySubscribers();
    this.notifyStatsSubscribers();

    return id;
  }

  /**
   * Get all log entries
   */
  getLogs(): EnhancedLogEntry[] {
    return this.ringBuffer.getAll();
  }

  /**
   * Get filtered log entries
   */
  getFilteredLogs(filter: {
    source?: DemoLogSource;
    level?: DemoLogLevel;
    action?: DemoLogAction;
    since?: string;
    searchText?: string;
    limit?: number;
  }): EnhancedLogEntry[] {
    let logs = this.ringBuffer.getAll();

    if (filter.source) {
      logs = logs.filter(log => log.source === filter.source);
    }

    if (filter.level) {
      logs = logs.filter(log => log.level === filter.level);
    }

    if (filter.action) {
      logs = logs.filter(log => log.action === filter.action);
    }

    if (filter.since) {
      const sinceTime = new Date(filter.since).getTime();
      logs = logs.filter(log => new Date(log.ts).getTime() >= sinceTime);
    }

    if (filter.searchText) {
      const searchLower = filter.searchText.toLowerCase();
      logs = logs.filter(log =>
        log.text.toLowerCase().includes(searchLower) ||
        log.source.toLowerCase().includes(searchLower) ||
        (log.action && log.action.toLowerCase().includes(searchLower))
      );
    }

    if (filter.limit) {
      logs = logs.slice(-filter.limit);
    }

    return logs;
  }

  /**
   * Get logger statistics
   */
  getStats(): LoggerStats {
    const bufferStats = this.ringBuffer.getStats();
    const lastLog = this.ringBuffer.peekLatest();

    return {
      totalLogs: this.logCounter,
      logsPerSource: { ...this.sourceCounters },
      logsPerLevel: { ...this.levelCounters },
      memoryUsageKB: bufferStats.memoryUsageKB,
      bufferUtilization: bufferStats.utilizationPercent,
      sessionStartTime: this.sessionStartTime,
      lastLogTime: lastLog?.ts
    };
  }

  /**
   * Export logs in various formats
   */
  exportLogs(format: 'json' | 'csv' | 'txt' = 'json'): { content: string; filename: string; stats: LoggerStats } {
    const logs = this.getLogs();
    const stats = this.getStats();
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');

    let content: string;
    let filename: string;

    switch (format) {
      case 'csv':
        content = this.formatLogsAsCsv(logs);
        filename = `demo-viewer-logs-${timestamp}.csv`;
        break;
      case 'txt':
        content = this.formatLogsAsText(logs);
        filename = `demo-viewer-logs-${timestamp}.txt`;
        break;
      default:
        content = JSON.stringify({
          metadata: {
            sessionId: this.sessionId,
            exportTime: new Date().toISOString(),
            totalLogs: logs.length,
            stats
          },
          logs
        }, null, 2);
        filename = `demo-viewer-logs-${timestamp}.json`;
    }

    return { content, filename, stats };
  }

  /**
   * Download logs as a file
   */
  downloadLogs(format: 'json' | 'csv' | 'txt' = 'json'): void {
    const { content, filename } = this.exportLogs(format);

    const blob = new Blob([content], {
      type: format === 'json' ? 'application/json' : 'text/plain'
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    this.log('system', 'info', 'Logs exported', {
      format,
      filename,
      size: content.length
    });
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    const clearedCount = this.ringBuffer.size;
    this.ringBuffer.clear();

    // Reset counters
    Object.keys(this.sourceCounters).forEach(source => {
      this.sourceCounters[source as DemoLogSource] = 0;
    });
    Object.keys(this.levelCounters).forEach(level => {
      this.levelCounters[level as DemoLogLevel] = 0;
    });

    this.logCounter = 0;

    this.log('system', 'info', 'Logs cleared', { clearedCount });

    this.notifySubscribers();
    this.notifyStatsSubscribers();
  }

  /**
   * Subscribe to log updates
   */
  subscribe(callback: (entries: EnhancedLogEntry[]) => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  /**
   * Subscribe to stats updates
   */
  subscribeToStats(callback: (stats: LoggerStats) => void): () => void {
    this.statsSubscribers.add(callback);
    return () => this.statsSubscribers.delete(callback);
  }

  private generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateLogId(): string {
    return `log_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  }

  private notifySubscribers(): void {
    const logs = this.getLogs();
    this.subscribers.forEach(callback => {
      try {
        callback(logs);
      } catch (error) {
        console.error('Error in log subscriber:', error);
      }
    });
  }

  private notifyStatsSubscribers(): void {
    const stats = this.getStats();
    this.statsSubscribers.forEach(callback => {
      try {
        callback(stats);
      } catch (error) {
        console.error('Error in stats subscriber:', error);
      }
    });
  }

  private autoExport(): void {
    try {
      const { content, filename } = this.exportLogs('json');

      // Store in browser storage as backup
      const storageKey = `demo-logs-backup-${Date.now()}`;
      try {
        localStorage.setItem(storageKey, content);

        // Clean up old backups (keep only last 3)
        const backupKeys = Object.keys(localStorage)
          .filter(key => key.startsWith('demo-logs-backup-'))
          .sort()
          .slice(0, -3);

        backupKeys.forEach(key => localStorage.removeItem(key));

      } catch (storageError) {
        console.warn('Failed to backup logs to localStorage:', storageError);
      }

      this.log('system', 'info', 'Auto-export completed', {
        filename,
        size: content.length,
        storageKey
      });

    } catch (error) {
      console.error('Auto-export failed:', error);
    }
  }

  private formatLogsAsCsv(logs: EnhancedLogEntry[]): string {
    const headers = ['timestamp', 'level', 'source', 'action', 'text', 'component', 'correlationId'];
    const rows = logs.map(log => [
      log.ts,
      log.level,
      log.source,
      log.action || '',
      `"${log.text.replace(/"/g, '""')}"`,
      log.component || '',
      log.correlationId || ''
    ]);

    return [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  }

  private formatLogsAsText(logs: EnhancedLogEntry[]): string {
    return logs.map(log => {
      const timestamp = log.ts;
      const level = log.level.toUpperCase().padEnd(5);
      const source = log.source.padEnd(12);
      const action = log.action ? `[${log.action}]` : '';
      return `${timestamp} ${level} ${source} ${action} ${log.text}`;
    }).join('\n');
  }
}

// Global logger instance
export const centralLogger = new CentralizedLogger({
  maxEntries: 1000,
  enablePerformanceTracking: import.meta.env.DEV === true,
  enableAutoExport: false,
  autoExportThreshold: 0.9
});

// Convenience functions for common logging patterns
export const logInfo = (source: DemoLogSource, text: string, meta?: any) =>
  centralLogger.log(source, 'info', text, meta);

export const logError = (source: DemoLogSource, text: string, error?: Error, meta?: any) =>
  centralLogger.log(source, 'error', text, {
    ...meta,
    error: error ? { message: error.message, stack: error.stack } : undefined
  });

export const logDebug = (source: DemoLogSource, text: string, meta?: any) =>
  centralLogger.log(source, 'debug', text, meta);

export const logPerformance = (source: DemoLogSource, action: DemoLogAction, startTime: number, meta?: any) =>
  centralLogger.log(source, 'info', `${action} completed`, meta, { action, startTime });

export { CentralizedLogger };